import { Alert, Button } from './ui/Primitives';
import React, { useEffect, useRef, useState } from 'react';
import { getOnboardingBrowserCallToken, getAdminBrowserCallToken } from '../services/onboardingApi';
import { createBrowserVoiceDevice } from '../services/twilioBrowserVoice';
import './BrowserTestCall.css';

const callStatusCopy = {
  idle: 'Your microphone will turn on after you start the call.',
  preparing: 'Requesting secure call access…',
  initializing: 'Starting secure audio…',
  connecting: 'Connecting to your Receptionist…',
  connected: 'Call in progress',
  ending: 'Ending the call…',
  ended: 'Call complete. Confirming your setup…',
};

const CALL_CONNECTION_TIMEOUT_MS = 20000;

function connectWithTimeout(device, options) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Call connection timed out')), CALL_CONNECTION_TIMEOUT_MS);
  });
  return Promise.race([device.connect(options), timeout])
    .finally(() => clearTimeout(timeoutId));
}

function allowStatusPaint() {
  return new Promise(resolve => setTimeout(resolve, 0));
}

function browserCallError(error) {
  const name = String(error?.name || '');
  const message = String(error?.message || '').toLowerCase();
  if (name === 'NotAllowedError' || message.includes('permission') || message.includes('microphone')) {
    return 'Microphone access is required. Allow access in your browser and try again.';
  }
  return 'The browser call could not connect. Check your microphone and try again.';
}

export default function BrowserTestCall({ onCallEnded, mode = 'onboarding' }) {
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const mountedRef = useRef(true);
  const finishedRef = useRef(false);
  const attemptRef = useRef(0);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      attemptRef.current += 1;
      callRef.current?.disconnect();
      deviceRef.current?.destroy();
    };
  }, []);

  function destroyDevice() {
    callRef.current = null;
    deviceRef.current?.destroy();
    deviceRef.current = null;
  }

  async function finishCall() {
    if (finishedRef.current) return;
    finishedRef.current = true;
    destroyDevice();
    if (!mountedRef.current) return;
    setStatus('ended');
    await onCallEnded?.();
  }

  function failCall(callError) {
    destroyDevice();
    if (!mountedRef.current) return;
    setError(browserCallError(callError));
    setStatus('idle');
  }

  async function startCall() {
    const attempt = attemptRef.current + 1;
    attemptRef.current = attempt;
    setError('');
    setStatus('preparing');
    finishedRef.current = false;
    try {
      const { token } = await (mode === 'admin' ? getAdminBrowserCallToken() : getOnboardingBrowserCallToken());
      if (!token) throw new Error('Voice token missing');
      if (attemptRef.current !== attempt || !mountedRef.current) return;
      setStatus('initializing');
      await allowStatusPaint();
      if (attemptRef.current !== attempt || !mountedRef.current) return;
      const device = createBrowserVoiceDevice(token);
      deviceRef.current = device;
      device.on('error', callError => attemptRef.current === attempt && failCall(callError));
      setStatus('connecting');
      const call = await connectWithTimeout(device, {
        params: { purpose: mode === 'admin' ? 'admin_test' : 'onboarding_test' },
        rtcConstraints: { audio: true },
      });
      if (attemptRef.current !== attempt || !mountedRef.current) {
        call.disconnect();
        device.destroy();
        return;
      }
      callRef.current = call;
      call.on('accept', () => attemptRef.current === attempt && mountedRef.current && setStatus('connected'));
      call.on('disconnect', () => attemptRef.current === attempt && finishCall());
      call.on('cancel', () => attemptRef.current === attempt && finishCall());
      call.on('reject', () => attemptRef.current === attempt && finishCall());
      call.on('error', callError => attemptRef.current === attempt && failCall(callError));
    } catch (callError) {
      if (attemptRef.current === attempt) failCall(callError);
    }
  }

  function endCall() {
    setStatus('ending');
    if (callRef.current) {
      callRef.current.disconnect();
      return;
    }
    attemptRef.current += 1;
    destroyDevice();
    setStatus('idle');
  }

  const callActive = ['preparing', 'initializing', 'connecting', 'connected', 'ending'].includes(status);

  return (
    <section className={`browser-test-call ${callActive ? 'active' : ''}`} aria-label="Browser test call">
      <div className="browser-test-call-icon" aria-hidden="true">●</div>
      <div className="browser-test-call-copy">
        <strong>Call from this browser</strong>
        <p>{mode === 'admin'
          ? 'Test your saved receptionist from this browser using your microphone and speakers.'
          : 'Your Receptionist will answer your question, offer a follow-up, and demonstrate a short message intake.'}</p>
        <span className="browser-test-call-status" role="status">
          {status === 'connected' ? <i aria-hidden="true" /> : null}
          {mode === 'admin' && status === 'ended' ? 'Call complete.' : callStatusCopy[status]}
        </span>
      </div>
      <div className="browser-test-call-action">
        {callActive ? (
          <Button variant="danger" size="lg" onClick={endCall} disabled={status === 'ending'} loading={status === 'ending'} loadingLabel={"Ending…"}>
            {'End call'}
          </Button>
        ) : (
          <Button size="lg" onClick={startCall}>{status === 'ended' ? 'Start another test call' : 'Start test call'}</Button>
        )}
      </div>
      {error ? <Alert tone="danger" title="Call could not start" className="browser-test-call-error">{error}</Alert> : null}
    </section>
  );
}
